import { useEffect } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import scheduleServiceApi from '../api/schedule.service.api';
import { ATTENDANCE_MODES } from '../constants/schedule.constants';
import { type QueryOpts } from '../api/query-client';
import {
  attendanceKeys,
  invalidateEventAttendance,
  isExpectedQrError,
  isCheckedInResult,
  isExpectedSelfAttestError,
  mapQrError,
  mapSelfAttestError,
  nextCheckInsCursor,
  CHECK_IN_RESULT,
  DEFAULT_CHECK_INS_LIMIT,
  CHECK_INS_POLL_INTERVAL_MS,
  type AttendanceMode,
  type CheckInFilters,
  type CheckInResult,
  type CheckInsPage,
  type MyCheckIn,
} from './attendance.types';

// Re-export so existing `shared/hooks/attendance.hooks` imports keep resolving after
// the split into attendance.types + check-in-session.hooks.
export * from './attendance.types';
export { useCheckInSession, useCheckedInCount } from './check-in-session.hooks';

/**
 * Whether the current user is checked in to this event. Consumers that previously
 * relied on refetch-on-focus now stay fresh via invalidation from the check-in
 * mutations.
 */
export function useMyCheckIn(
  eventId: string | null | undefined,
  options: QueryOpts<MyCheckIn> = {},
) {
  return useQuery<MyCheckIn>({
    queryKey: attendanceKeys.myCheckIn(eventId),
    queryFn: () => scheduleServiceApi.getMyCheckIn({ eventId }) as Promise<MyCheckIn>,
    enabled: !!eventId,
    ...options,
  });
}

/**
 * The check-in list for an event, paginated by the BE's opaque cursor. Server-side
 * filters (`attendanceMode`/`method`/`userId`) narrow the set at the source. Pages
 * are flattened into `items`; `loadMore`/`hasMore` drive incremental loading so no
 * request silently caps the list. `total` is the BE's count for the current query —
 * the unfiltered total only when no filter is passed.
 *
 * Pass `all: true` for consumers that need the complete set with no list UI to
 * scroll (e.g. count summaries): remaining pages are drained automatically.
 *
 * `enabled` gates fetching; `polling` turns on a periodic refetch (and focus
 * refetch) for live views like an open check-in session.
 */
export function useCheckIns(
  eventId: string | null | undefined,
  {
    limit = DEFAULT_CHECK_INS_LIMIT,
    all = false,
    enabled = true,
    polling = false,
    ...filters
  }: { limit?: number; all?: boolean; enabled?: boolean; polling?: boolean } & CheckInFilters = {},
) {
  const {
    data,
    error,
    isFetched,
    isSuccess,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery<CheckInsPage>({
    queryKey: attendanceKeys.checkIns(eventId, { limit, ...filters }),
    queryFn: ({ pageParam }) =>
      scheduleServiceApi.getCheckIns({
        eventId,
        limit,
        cursor: pageParam as string | undefined,
        ...filters,
      }) as Promise<CheckInsPage>,
    enabled: enabled && !!eventId,
    initialPageParam: undefined,
    getNextPageParam: nextCheckInsCursor,
    refetchInterval: polling ? CHECK_INS_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: polling,
  });

  const canLoadMore = hasNextPage && !isFetchingNextPage;

  useEffect(() => {
    if (all && canLoadMore) fetchNextPage();
  }, [all, canLoadMore, fetchNextPage]);

  return {
    items: isFetched ? (data?.pages.flatMap(p => p.items) ?? []) : null,
    total: data?.pages.at(-1)?.total ?? 0,
    loaded: isSuccess,
    loading: isFetching && !isFetchingNextPage,
    loadingMore: isFetchingNextPage,
    hasMore: hasNextPage ?? false,
    error: error ?? null,
    loadMore: () => {
      if (canLoadMore) fetchNextPage();
    },
    refetch,
  };
}

/**
 * Submits an attendee self check-in via QR. Maps every documented BE error code to
 * a result variant (see CHECK_IN_RESULT); codeless errors fall through to
 * INVALID_CODE. On success, invalidates the event's attendance caches.
 */
export function useQrCheckIn(
  eventId: string | null | undefined,
  { onError }: { onError?: (error: unknown, context: Record<string, unknown>) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation<
    { result: CheckInResult },
    never,
    { code?: string; attendanceMode: AttendanceMode }
  >({
    mutationFn: async ({ code, attendanceMode }) => {
      if (!eventId || !attendanceMode) return { result: CHECK_IN_RESULT.INVALID_CODE };
      try {
        await scheduleServiceApi.selfCheckIn({ eventId, code, attendanceMode });
        return { result: CHECK_IN_RESULT.SUCCESS };
      } catch (error) {
        if (!isExpectedQrError(error)) {
          onError?.(error, { scope: 'qr-check-in', eventId });
        }
        return { result: mapQrError(error) };
      }
    },
    onSuccess: ({ result }) => {
      if (eventId && isCheckedInResult(result)) invalidateEventAttendance(qc, eventId);
    },
  });
}

/**
 * Submits an attendee self attestation (no code — the attendee just confirms
 * their own attendanceMode). Maps every documented BE error code to a result
 * variant (see CHECK_IN_RESULT); codeless/unexpected errors fall through to
 * FAILED. On success, invalidates the event's attendance caches.
 *
 * Unlike the other attendance mutations, this returns `{ submit, submitting }`
 * directly (not the raw mutation) — both web and mobile consume it as-is.
 */
export function useSelfAttestation(
  eventId: string | null | undefined,
  { onError }: { onError?: (error: unknown, context: Record<string, unknown>) => void } = {},
) {
  const qc = useQueryClient();
  const mutation = useMutation<
    { result: CheckInResult },
    never,
    { attendanceMode: AttendanceMode }
  >({
    mutationFn: async ({ attendanceMode }) => {
      if (!eventId || !attendanceMode) return { result: CHECK_IN_RESULT.FAILED };
      try {
        await scheduleServiceApi.selfAttest({ eventId, attendanceMode });
        return { result: CHECK_IN_RESULT.SUCCESS };
      } catch (error) {
        if (!isExpectedSelfAttestError(error)) {
          onError?.(error, { scope: 'self-attest', eventId });
        }
        return { result: mapSelfAttestError(error) };
      }
    },
    onSuccess: ({ result }) => {
      if (eventId && isCheckedInResult(result)) invalidateEventAttendance(qc, eventId);
    },
  });

  const submit = ({ attendanceMode }: { attendanceMode: AttendanceMode }) =>
    mutation.mutateAsync({ attendanceMode });

  return { submit, submitting: mutation.isPending };
}

/**
 * Manually checks one or more users in. The BE accepts a single user per request,
 * so we fan out with Promise.all (each call maps its own rejection to a failure)
 * to keep partial successes intact. Returns
 * the successful and failed user IDs separately. `attendanceMode` defaults to
 * IN_PERSON since manual check-in is opened by a physically-present host.
 */
export function useManualCheckIn(eventId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { successIds: string[]; failureIds: string[] },
    never,
    { userIds: string[]; attendanceMode?: AttendanceMode }
  >({
    mutationFn: async ({ userIds, attendanceMode = ATTENDANCE_MODES.IN_PERSON }) => {
      if (!eventId || !userIds?.length) return { successIds: [], failureIds: [] };

      const outcomes = await Promise.all(
        userIds.map(async userId => {
          const ok = await scheduleServiceApi
            .manualCheckIn({ eventId, userId, attendanceMode })
            .then(
              () => true,
              () => false,
            );
          return { userId, ok };
        }),
      );

      return {
        successIds: outcomes.filter(o => o.ok).map(o => o.userId),
        failureIds: outcomes.filter(o => !o.ok).map(o => o.userId),
      };
    },
    onSuccess: ({ successIds }) => {
      if (eventId && successIds.length) invalidateEventAttendance(qc, eventId);
    },
  });
}
